import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Check, BadgePercent, Truck, Headset, FileText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SEO } from "../components/SEO";
import { submitTradeAccount } from "../lib/api";

const inputCls = "w-full bg-white border border-vr-border rounded-lg px-4 py-3 text-vr-ink placeholder:text-vr-dim/60 focus:outline-none focus:border-vr-brand focus:ring-2 focus:ring-vr-brand/15 transition-all";
const INDUSTRIES = ["Café / Restaurant", "Supermarket / Grocery", "Manufacturing", "Healthcare / Aged Care", "Hospitality / Events", "Commercial Cleaning", "Other"];
const SPEND = ["Under $500 / month", "$500 – $2,000 / month", "$2,000 – $5,000 / month", "$5,000+ / month"];

const BENEFITS = [
  { icon: BadgePercent, t: "Wholesale pricing", d: "Volume-based trade rates across every category." },
  { icon: Truck, t: "Priority delivery", d: "Nationwide dispatch with account scheduling." },
  { icon: Headset, t: "Dedicated support", d: "A named account manager who knows your business." },
  { icon: FileText, t: "Consolidated billing", d: "One monthly account, less admin." },
];

export default function TradeAccount() {
  const [form, setForm] = useState({ business_name: "", contact_name: "", email: "", phone: "", industry: "", address: "", monthly_spend: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email || !form.phone) {
      toast.error("Please fill in business name, contact, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await submitTradeAccount(form);
      toast.success("Application received — we'll set up your account within one business day.");
      setForm({ business_name: "", contact_name: "", email: "", phone: "", industry: "", address: "", monthly_spend: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <main data-testid="trade-account-page" className="pt-[104px]">
      <SEO
        title="Open a Wholesale Trade Account | VR Packaging"
        description="Apply for a VR Packaging & Hygiene trade account for wholesale pricing, priority NZ-wide dispatch and a dedicated account manager. Approval within one business day."
        path="/trade-account"
      />
      <div className="bg-vr-navy text-white">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-14">
          <Breadcrumbs items={[{ label: "Trade Account" }]} />
          <h1 className="mt-6 font-display font-extrabold tracking-tight text-4xl md:text-6xl">Open a Trade Account</h1>
          <p className="mt-4 text-white/70 max-w-2xl">Apply for a wholesale account and unlock trade pricing, priority delivery and dedicated support. Approval usually within one business day.</p>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-16 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <div>
          <div className="grid sm:grid-cols-2 gap-5">
            {BENEFITS.map((b) => (
              <div key={b.t} className="rounded-2xl border border-vr-border bg-white p-6">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-vr-brand/10 text-vr-brand mb-4"><b.icon size={20} /></span>
                <h3 className="font-display font-bold text-vr-ink">{b.t}</h3>
                <p className="mt-1 text-sm text-vr-dim">{b.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl bg-vr-bg2 border border-vr-border p-6">
            <h3 className="font-display font-bold text-vr-ink mb-3">What you'll need</h3>
            {["Registered business name & address", "Contact person and phone/email", "Estimated monthly spend"].map((x) => (
              <div key={x} className="flex items-center gap-2 text-sm text-vr-dim py-1"><Check size={16} className="text-vr-brand" /> {x}</div>
            ))}
          </div>
        </div>

        <form onSubmit={submit} data-testid="trade-account-form" className="bg-white rounded-3xl border border-vr-border shadow-sm p-6 md:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2"><label className="block text-sm font-medium text-vr-ink mb-2">Business name *</label><input data-testid="trade-business" className={inputCls} value={form.business_name} onChange={set("business_name")} placeholder="Your company Ltd" /></div>
          <div><label className="block text-sm font-medium text-vr-ink mb-2">Contact name *</label><input data-testid="trade-contact" className={inputCls} value={form.contact_name} onChange={set("contact_name")} placeholder="Full name" /></div>
          <div><label className="block text-sm font-medium text-vr-ink mb-2">Phone *</label><input data-testid="trade-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+64 ..." /></div>
          <div><label className="block text-sm font-medium text-vr-ink mb-2">Email *</label><input data-testid="trade-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.co.nz" /></div>
          <div>
            <label className="block text-sm font-medium text-vr-ink mb-2">Industry</label>
            <Select value={form.industry} onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}>
              <SelectTrigger data-testid="trade-industry" className={`${inputCls} h-auto`}><SelectValue placeholder="Select industry" /></SelectTrigger>
              <SelectContent>{INDUSTRIES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><label className="block text-sm font-medium text-vr-ink mb-2">Business address</label><input data-testid="trade-address" className={inputCls} value={form.address} onChange={set("address")} placeholder="Street, suburb, city" /></div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-vr-ink mb-2">Estimated monthly spend</label>
            <Select value={form.monthly_spend} onValueChange={(v) => setForm((f) => ({ ...f, monthly_spend: v }))}>
              <SelectTrigger data-testid="trade-spend" className={`${inputCls} h-auto`}><SelectValue placeholder="Select range" /></SelectTrigger>
              <SelectContent>{SPEND.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><label className="block text-sm font-medium text-vr-ink mb-2">Anything else?</label><textarea data-testid="trade-message" rows={3} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="Products of interest, delivery notes…" /></div>
          <div className="sm:col-span-2">
            <button type="submit" data-testid="trade-submit" disabled={loading} className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-vr-brand text-white font-semibold hover:bg-vr-branddark transition-colors disabled:opacity-60">
              {loading && <Loader2 className="animate-spin" size={18} />}{loading ? "Submitting…" : "Submit application"}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
